import { UnsavedDataPrompt } from "components/Routes/unsaved-data-prompt";
import { AvailablePlatform, usePlatformContext } from "providers/Auth/platform.provider";
import { FunctionComponent, useEffect } from "react";
import { Redirect, Switch, useHistory, useLocation } from "react-router-dom";
import { BaseRoutes } from "routes/base.routes";
import config from "./config/config";
import { DashboardActionProvider } from "./pages/dashboard/dashboard-action.provider";
import { PomRoutes } from "./routes/pom.routes";
import { ScrollToTopButton } from "./components/scroll-to-top/scroll-to-top-button.component";
import { GroupType, pomGroupTypes } from "./domain/group/group-type";
import { useScrollToTopContext } from "./domain/scroll/scroll-to-top-context";
import { isAdmin, isPomMainContact } from "./domain/user/user";
import { SidebarLayout } from "./layout/sidebar.layout";
import { DashboardPage } from "./pages/dashboard/dashboard.page";
import { getSessionEmail, isUserLoggedIn } from "./utils/auth";
import { useSessionKeepalive } from "./hooks/use-session-keepalive";
import { AuthenticatedRoute } from "./utils/AuthenticatedRoute";
import {
  AvailablePomRoutes,
  AvailableBaseRoutes,
  isPomRoute,
} from "./utils/constants";
import { ConfigProvider } from "./domain/config/config.provider";
import { AuthProvider, AuthProviderContext } from "./providers/Auth/auth.provider";
import { MaintenanceModePage } from "pages/maintenance-mode/maintenance-mode.page";

export const Portal: FunctionComponent = () => {
  const location = useLocation();
  const history = useHistory();
  const { setActivePlatform } = usePlatformContext();
  const { isVisible: isScrollToTopVisible } = useScrollToTopContext();

  // Mounted once for the whole authenticated area, so time spent working in a
  // form without hitting the API still counts as activity.
  useSessionKeepalive();

  useEffect(() => {
    if (isPomRoute(location.pathname)) {
      setActivePlatform(AvailablePlatform.Pom);
    }
  }, [location.pathname, setActivePlatform]);

  // Read before isUserLoggedIn(), which drops the token once the session has expired.
  const nextOwner = getSessionEmail();

  if (!isUserLoggedIn()) {
    // Full relative URL, kept in step with AuthenticatedRoute and the 401 interceptor.
    history.push(AvailableBaseRoutes.Login, {
      next: `${location.pathname}${location.search}${location.hash}`,
      nextOwner,
    });
    return null;
  }

  return (
    <AuthProvider>
      <AuthProviderContext.Consumer>
        {({internalUser}) => {
          if (!internalUser) {
            return <></>;
          }

          if (config.maintenanceMode && !isAdmin(internalUser)) {
            return <MaintenanceModePage/>;
          }

          return (
            <ConfigProvider>
              <UnsavedDataPrompt/>
              <Switch>
                <DashboardActionProvider>
                  <SidebarLayout>
                    <Switch>
                      {/* Dashboard for all user groups */}
                      <AuthenticatedRoute
                        allowedRoles={[GroupType.Admin, ...pomGroupTypes]}
                        exact
                        path={AvailableBaseRoutes.Portal}
                        component={DashboardPage}
                        allowedConditionCallback={isPomMainContact}
                      />

                      {/* POM */}
                      <AuthenticatedRoute
                        allowedRoles={pomGroupTypes}
                        path={AvailablePomRoutes.Portal}
                        allowedConditionCallback={isPomMainContact}
                      >
                        <PomRoutes/>
                      </AuthenticatedRoute>

                      {/* SHARED */}
                      <AuthenticatedRoute
                        path={AvailableBaseRoutes.Portal}
                        allowedConditionCallback={isPomMainContact}
                      >
                        <BaseRoutes/>
                      </AuthenticatedRoute>
                    </Switch>
                  </SidebarLayout>
                </DashboardActionProvider>

                <Redirect path="*" to={AvailableBaseRoutes.Portal} push/>
              </Switch>
              {isScrollToTopVisible && <ScrollToTopButton/>}
            </ConfigProvider>
          );
        }}
      </AuthProviderContext.Consumer>
    </AuthProvider>
  );
};
